import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AuthLayout, InputField, Button } from "../components/common";
import { useAuth } from "../contexts/AuthContext";

const ForgotPassword = () => {
  const { forgotPassword, isLoading, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear global error
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await forgotPassword({ email: formData.email });
        setIsSubmitted(true);
      } catch (error) {
        // Error is handled by the AuthContext
        console.error("Forgot password failed:", error);
      }
    }
  };

  const subtitle = (
    <>
      Remember your password?{" "}
      <Link
        to="/signin"
        className="font-medium text-cyan-500 hover:text-cyan-400 transition-colors duration-200"
      >
        Sign in here
      </Link>
    </>
  );

  if (isSubmitted) {
    return (
      <AuthLayout title="Check your email" subtitle={subtitle}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Password reset email sent
          </h3>
          <p className="text-gray-600 mb-6">
            We've sent a password reset link to{" "}
            <strong>{formData.email}</strong>. Please check your email and
            follow the instructions to reset your password.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => setIsSubmitted(false)}
              variant="outline"
              fullWidth
            >
              Try another email
            </Button>
            <Link to="/signin">
              <Button variant="primary" fullWidth>
                Back to sign in
              </Button>
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" subtitle={subtitle}>
      <div className="text-center mb-6">
        <p className="text-gray-600">
          Enter your email address and we'll send you a link to reset your
          password.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <form className="space-y-6" onSubmit={handleSubmit}>
        <InputField
          id="email"
          name="email"
          type="email"
          label="Email address"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleInputChange}
          error={errors.email}
          required
          autoComplete="email"
        />

        <Button
          type="submit"
          size="lg"
          fullWidth
          isLoading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? "Sending..." : "Send reset link"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;
